import React, { useState, useRef, useCallback, useEffect, useContext } from "react";
import { compose } from "recompose";
import { useHistory } from "react-router-dom";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Grid, useMediaQuery, useTheme } from "@material-ui/core";
import { withAuthorization, AuthUserContext } from "../Session";
import { withFirebase } from "../Firebase";
import NavigationMenus from "../NavigationMenus";
import Helpers from "../Helpers";
import CustomButton from "../Button";
import * as ROUTES from "../../constants/route";
import { getVideoExtension } from "./videoFormat";
import {
  AttestationStatusNotice,
  isAttestationEligible,
  useAttestationEligibility,
} from "./useAttestationEligibility";
import { useAttestationDetails, useIdPreview } from "./useAttestationDetails";
import { useMediaRecorder } from "./useMediaRecorder";
import {
  AttestationCard,
  IdPreviewDialog,
  PrepareStep,
  RecordStep,
  ReviewStep,
} from "./AttestationSteps";

const MAX_DURATION_SECONDS = 60;
// The 1st-gen v2 Cloud Function rejects request bodies over about 10 MB before the API runs.
const MAX_UPLOAD_BYTES = 9_500_000;

const VideoAttestationBase = (props) => {
  const authUser = useContext(AuthUserContext);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isEmbedded = Boolean(props.embedded || props.nextStep);
  const uploadContext = isEmbedded ? "long-kyc" : "video-only";
  const account = useAttestationEligibility(props.firebase, authUser?.uid, uploadContext);
  const { refresh: refreshAccount, eligibleRef } = account;
  const details = useAttestationDetails(props.firebase, authUser?.uid);
  const idPreview = useIdPreview(details.submittedFrontIdPath);
  const camera = useMediaRecorder({
    eligibleRef,
    maxDurationSeconds: MAX_DURATION_SECONDS,
    maxRecordingBytes: MAX_UPLOAD_BYTES,
  });
  const { startCamera, stopCamera, stopRecording } = camera;
  const [stateRefused, setStateRefused] = useState(false);
  const [recordingState, setRecordingState] = useState("idle");
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [checkingAction, setCheckingAction] = useState(false);
  const [isReplacingExistingVideo, setIsReplacingExistingVideo] = useState(false);
  const actionRef = useRef(false);
  const helpersRef = useRef(new Helpers());

  const hasExistingVideo = Boolean(details.existingVideoUrl) && !isReplacingExistingVideo;
  const hasCompletedAttestation = hasExistingVideo || details.isAttestationExcluded;
  const isBlocked = !account.eligible || stateRefused || Boolean(details.error);
  const isBusy = account.checking || checkingAction;

  useEffect(() => {
    if (!isBlocked) return;
    stopRecording();
    stopCamera();
  }, [isBlocked, stopRecording, stopCamera]);

  useEffect(() => {
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  useEffect(() => {
    if (props.onStatusChange) {
      props.onStatusChange(recordingState === "success" || hasCompletedAttestation);
    }
  }, [props.onStatusChange, recordingState, hasCompletedAttestation]);

  useEffect(() => {
    if ((recordingState !== "success" && !details.isAttestationExcluded) || isEmbedded ||
        !account.loaded || account.error || details.error) {
      return undefined;
    }
    const redirectTimer = setTimeout(() => history.replace(ROUTES.DASHBOARD), 2500);
    return () => clearTimeout(redirectTimer);
  }, [history, isEmbedded, details.isAttestationExcluded, recordingState, account.loaded, account.error, details.error]);

  const checkEligibility = useCallback(async () => {
    const data = await refreshAccount();
    return data && eligibleRef.current && isAttestationEligible(data.stats, uploadContext);
  }, [refreshAccount, eligibleRef, uploadContext]);

  // Every user action re-reads the root account first and runs one at a time.
  const runEligibleAction = useCallback(async (action) => {
    if (actionRef.current || stateRefused) return;
    actionRef.current = true;
    setCheckingAction(true);
    try {
      if (await checkEligibility()) await action();
    } finally {
      actionRef.current = false;
      setCheckingAction(false);
    }
  }, [checkEligibility, stateRefused]);

  const handleRecordingComplete = useCallback((recording) => {
    if (!recording) {
      setErrorMessage("Your browser recorded an unsupported video format. Please try another browser.");
      setRecordingState("error");
      return;
    }
    if (recording.size > MAX_UPLOAD_BYTES) {
      setErrorMessage("Your recording is too large to upload. Please re-record a shorter video.");
      setRecordingState("error");
      return;
    }
    setRecordedBlob(recording);
    setRecordedUrl(URL.createObjectURL(recording));
    setRecordingState("preview");
  }, []);

  const resetRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setErrorMessage("");
  };

  const handleEnableCamera = () => runEligibleAction(startCamera);

  const handleStartRecording = () => runEligibleAction(() => {
    resetRecording();
    if (camera.startRecording(handleRecordingComplete)) setRecordingState("recording");
  });

  const handleReRecord = () => runEligibleAction(async () => {
    resetRecording();
    setRecordingState("idle");
    setIsReplacingExistingVideo(true);
    await startCamera();
  });

  const handleUpload = () => {
    if (!recordedBlob || !authUser?.uid) return;
    return runEligibleAction(async () => {
      setRecordingState("uploading");
      setErrorMessage("");
      try {
        const extension = getVideoExtension(recordedBlob.type);
        if (!extension) throw new Error("Unsupported recorded video format");

        const formData = new FormData();
        formData.append("video", recordedBlob, `attestation-video.${extension}`);
        formData.append("uploadContext", uploadContext);
        await axios.post(`${ROUTES.FUNCTION_BASE_URL}v2/attestation/${authUser.uid}`, formData, {
          headers: { Authorization: `Bearer ${helpersRef.current.getCookie("token")}` },
        });

        setRecordingState("success");
        // Older successful responses have no data.status; always read the root doc.
        await refreshAccount();
      } catch (err) {
        if ([403, 409].includes(err.response?.status)) {
          setStateRefused(true);
          setRecordingState("preview");
          await refreshAccount();
          return;
        }
        setRecordingState("error");
        setErrorMessage(err.response?.data?.message || "Failed to upload video. Please try again.");
      }
    });
  };

  const handleContinue = () => runEligibleAction(() => {
    props.fixedStep(5);
    props.nextStep();
  });

  const embeddedFooter = props.nextStep && (
    <Grid item xs={12} style={{ marginTop: 24 }}>
      <div className="flex">
        <div className="btn-back" onClick={props.prevStep}>
          Previous Step
        </div>
        <CustomButton
          text="Continue"
          type="button"
          buttonTheme="primary"
          disabled={isBusy || (recordingState !== "success" && !hasCompletedAttestation)}
          onClick={handleContinue}
        />
      </div>
    </Grid>
  );

  if (!account.loaded || isBlocked) {
    return (
      <AttestationStatusNotice
        account={{ ...account, error: details.error || account.error || (stateRefused && account.eligible
          ? "Your account cannot submit a video right now. Refresh your status or contact online@rampver.com for help."
          : "") }}
        history={history}
        onRetry={async () => {
          await refreshAccount();
          if (details.error) details.retry();
          setStateRefused(false);
        }}
      >
        {details.isAttestationExcluded && <p>Your video attestation has been waived.</p>}
        {details.existingVideoUrl && (
          <video
            aria-label="Your submitted video"
            src={details.existingVideoUrl}
            controls
            playsInline
            preload="metadata"
            style={{ width: "100%", maxWidth: isMobile ? 320 : 640 }}
          />
        )}
      </AttestationStatusNotice>
    );
  }

  if (details.loading) {
    return <div role="status"><CircularProgress size={24} /> Loading your verification details...</div>;
  }

  if (details.isAttestationExcluded) {
    return (
      <div className="py-6 md:py-8">
        <AttestationCard>
          <p className="text-gray-700 m-0">
            Your video attestation has been waived. You can continue with your verification.
          </p>
        </AttestationCard>
        {embeddedFooter}
      </div>
    );
  }

  const activeStep = getActiveStep({ recordingState, hasExistingVideo, cameraStatus: camera.cameraStatus });

  return (
    <div className="py-6 md:py-8">
      <AttestationCard activeStep={activeStep}>
        {activeStep === "prepare" && (
          <PrepareStep
            isMobile={isMobile}
            script={details.script}
            disabled={isBusy}
            onEnableCamera={handleEnableCamera}
            onPreviewId={idPreview.open}
          />
        )}
        {activeStep === "record" && (
          <RecordStep
            isMobile={isMobile}
            script={details.script}
            cameraStatus={camera.cameraStatus}
            isRecording={recordingState === "recording"}
            secondsLeft={MAX_DURATION_SECONDS - camera.elapsedSeconds}
            attachLiveVideo={camera.attachLiveVideo}
            disabled={isBusy}
            onStartRecording={handleStartRecording}
            onStopRecording={stopRecording}
            onRetryCamera={handleEnableCamera}
            onPreviewId={idPreview.open}
          />
        )}
        {activeStep === "review" && (
          <ReviewStep
            isMobile={isMobile}
            variant={hasExistingVideo ? "existing" : recordingState}
            videoUrl={hasExistingVideo ? details.existingVideoUrl : recordedUrl}
            isEmbedded={isEmbedded}
            errorMessage={errorMessage}
            disabled={isBusy}
            canRetryUpload={Boolean(recordedBlob)}
            onReRecord={handleReRecord}
            onSubmit={handleUpload}
          />
        )}
      </AttestationCard>
      {embeddedFooter}
      <IdPreviewDialog {...idPreview} />
    </div>
  );
};

const getActiveStep = ({ recordingState, hasExistingVideo, cameraStatus }) => {
  if (recordingState === "recording") return "record";
  if (recordingState !== "idle" || hasExistingVideo) return "review";
  return cameraStatus === "off" ? "prepare" : "record";
};

export { VideoAttestationBase as VideoAttestationStep };

const condition = (authUser) => !!authUser;

const VideoAttestation = compose(
  withAuthorization(condition),
  withFirebase
)(VideoAttestationBase);

const VideoAttestationPage = () => (
  <NavigationMenus content={<VideoAttestation />} disableFab />
);

export default VideoAttestationPage;
